import React, { useState, useEffect } from 'react';
import { Star, Quote, MapPin, Building } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('Tous');

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      if (response.data && response.data.success) {
        setTestimonials(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  // Get unique project types for filtering
  const projectTypes = ['Tous', ...new Set(testimonials.map(t => t.project_type).filter(Boolean))];
  
  // Filter testimonials
  const filteredTestimonials = selectedType === 'Tous' 
    ? testimonials 
    : testimonials.filter(t => t.project_type === selectedType);

  // Separate featured and regular testimonials
  const featuredTestimonials = filteredTestimonials.filter(t => t.is_featured);
  const regularTestimonials = filteredTestimonials.filter(t => !t.is_featured);

  if (loading) {
    return (
      <section
        className="py-16"
        style={{
          backgroundColor: '#FFFFFF',
          backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px)',
          backgroundSize: '4px 4px'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4" style={{ borderColor: '#f97316' }}></div>
            <p className="text-gray-600">Chargement des témoignages...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16"
      id="testimonials"
      style={{
        backgroundColor: '#FFFFFF',
        backgroundImage: 'radial-gradient(rgba(15, 23, 42, 0.05) 1px, transparent 1px)',
        backgroundSize: '4px 4px'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: '#f97316' }}>
              <Quote className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Ils Me Font Confiance
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez les témoignages de mes clients web et vidéo, de Saint-Quentin à toute la France.
          </p>
        </div>

        {/* Project Type Filter */}
        {projectTypes.length > 2 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {projectTypes.map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedType === type
                    ? 'text-white'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
                style={{
                  backgroundColor: selectedType === type ? '#f97316' : undefined
                }}
              >
                {type}
              </button>
            ))}
          </div>
        )}

        {/* Featured Testimonials */}
        {featuredTestimonials.length > 0 && (
          <div className="mb-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Témoignages Mis en Avant</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredTestimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border-2 border-orange-200">
                  <div className="flex items-center mb-4">
                    <div className="flex mr-2">
                      {renderStars(testimonial.rating)}
                    </div>
                    <span className="text-sm font-medium text-orange-600">
                      {testimonial.project_type}
                    </span>
                  </div>
                  
                  <blockquote className="text-gray-700 mb-4 italic">
                    "{testimonial.testimonial_text}"
                  </blockquote>
                  
                  <div className="border-t border-orange-200 pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {testimonial.client_name}
                        </p>
                        {testimonial.client_title && (
                          <p className="text-sm text-gray-600">
                            {testimonial.client_title}
                          </p>
                        )}
                        {testimonial.client_company && (
                          <div className="flex items-center mt-1 text-sm text-gray-600">
                            <Building className="h-3 w-3 mr-1" />
                            {testimonial.client_company}
                          </div>
                        )}
                        {testimonial.client_location && (
                          <div className="flex items-center mt-1 text-sm text-gray-600">
                            <MapPin className="h-3 w-3 mr-1" />
                            {testimonial.client_location}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Regular Testimonials */}
        {regularTestimonials.length > 0 && (
          <div>
            {featuredTestimonials.length > 0 && (
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Autres Témoignages</h3>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularTestimonials.map((testimonial) => (
                <div key={testimonial.id} className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="flex mr-2">
                      {renderStars(testimonial.rating)}
                    </div>
                    <span className="text-sm font-medium text-gray-600">
                      {testimonial.project_type}
                    </span>
                  </div>
                  
                  <blockquote className="text-gray-700 mb-4 italic">
                    "{testimonial.testimonial_text}"
                  </blockquote>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {testimonial.client_name}
                        </p>
                        {testimonial.client_title && (
                          <p className="text-sm text-gray-600">
                            {testimonial.client_title}
                          </p>
                        )}
                        {testimonial.client_company && (
                          <div className="flex items-center mt-1 text-sm text-gray-600">
                            <Building className="h-3 w-3 mr-1" />
                            {testimonial.client_company}
                          </div>
                        )}
                        {testimonial.client_location && (
                          <div className="flex items-center mt-1 text-sm text-gray-600">
                            <MapPin className="h-3 w-3 mr-1" />
                            {testimonial.client_location}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredTestimonials.length === 0 && (
          <div className="text-center py-12">
            <Quote className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun témoignage disponible</h3>
            <p className="text-gray-600">
              {selectedType === 'Tous' 
                ? 'Aucun témoignage n\'est disponible pour le moment.'
                : `Aucun témoignage pour le type "${selectedType}".`
              }
            </p>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gray-50 rounded-lg p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Rejoignez Mes Clients Satisfaits
            </h3>
            <p className="text-gray-600 mb-6">
              Prêt à lancer votre site, votre boutique ou une production vidéo impactante ?
            </p>
            <button
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/#contact';
                }
              }}
              className="inline-flex items-center px-6 py-3 text-base font-medium rounded-md text-white transition-colors"
              style={{ backgroundColor: '#f97316' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#ea580c'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#f97316'}
            >
              Discutons de Votre Projet
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
